import type * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTheme } from "next-themes";
import {
  Activity,
  Droplets,
  Fan,
  Lamp,
  Lightbulb,
  Moon,
  PartyPopper,
  Plug,
  Shield,
  Sun,
  Thermometer,
  Tv,
  Zap,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { NotFoundPage } from "@/pages/error";
import {
  AnimatedNumber,
  ClimateTile,
  ConnectionBanner,
  DeviceTile,
  DimmerSlider,
  EnergyGauge,
  FlowButton,
  GlanceCard,
  IconBubble,
  InsightChart,
  KioskClock,
  KioskScreensaver,
  KioskToggle,
  MeterRow,
  PillTabs,
  SensorBadge,
  StatusBar,
  StatusPill,
  ThermostatDial,
  Tile,
  ZoneSection,
  type Domain,
} from "@/components/system";
import type { HomeySocketStatus } from "@/types/homey";

const IS_DEV_HOST =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

const DOMAINS: Domain[] = ["neutral", "light", "climate", "energy", "security", "flow"];

const TOKEN_SWATCHES = [
  { name: "background", cls: "bg-background border" },
  { name: "tile", cls: "bg-tile border" },
  { name: "primary", cls: "bg-primary" },
  { name: "muted", cls: "bg-muted" },
  { name: "domain-light", cls: "bg-domain-light" },
  { name: "domain-light-wash", cls: "bg-domain-light-wash" },
  { name: "domain-climate", cls: "bg-domain-climate" },
  { name: "domain-climate-wash", cls: "bg-domain-climate-wash" },
  { name: "domain-energy", cls: "bg-domain-energy" },
  { name: "domain-energy-wash", cls: "bg-domain-energy-wash" },
  { name: "domain-security", cls: "bg-domain-security" },
  { name: "domain-security-wash", cls: "bg-domain-security-wash" },
  { name: "domain-flow", cls: "bg-domain-flow" },
  { name: "domain-flow-wash", cls: "bg-domain-flow-wash" },
];

const CHART_DATA = [
  { t: "00:00", value: 220 },
  { t: "04:00", value: 180 },
  { t: "08:00", value: 540 },
  { t: "12:00", value: 870 },
  { t: "16:00", value: 640 },
  { t: "20:00", value: 1120 },
  { t: "23:00", value: 380 },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="border-b pb-2 text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

export function DesignSystemPage() {
  const { resolvedTheme, setTheme } = useTheme();
  const [dim, setDim] = useState(0.65);
  const [lampOn, setLampOn] = useState(true);
  const [targetTemp, setTargetTemp] = useState(21);
  const [power, setPower] = useState(740);
  const [showClock, setShowClock] = useState(false);
  const [bannerSocket, setBannerSocket] = useState<HomeySocketStatus>("open");
  const [kioskFullscreen, setKioskFullscreen] = useState(false);
  const [screensaverShow, setScreensaverShow] = useState(false);
  const [screensaverStyle, setScreensaverStyle] = useState<"clock" | "blank">("clock");
  const [bannerHomey, setBannerHomey] = useState<"connected" | "disconnected">("connected");
  const [pillTab, setPillTab] = useState("home");
  const [climateTarget, setClimateTarget] = useState(21);
  const screensaverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-dismiss the screensaver preview after ~3s.
  useEffect(() => {
    if (screensaverShow) {
      screensaverTimerRef.current = setTimeout(() => setScreensaverShow(false), 3000);
    } else if (screensaverTimerRef.current !== null) {
      clearTimeout(screensaverTimerRef.current);
      screensaverTimerRef.current = null;
    }
    return () => {
      if (screensaverTimerRef.current !== null) {
        clearTimeout(screensaverTimerRef.current);
      }
    };
  }, [screensaverShow]);

  if (!IS_DEV_HOST) return <NotFoundPage />;

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-10 p-6 pb-24">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Homeloupe Design System</h1>
          <p className="text-sm text-muted-foreground">
            Dev-only showcase. App pages compose only these components.
          </p>
        </div>
        <Button
          variant="outline"
          size="icon"
          aria-label="Toggle theme"
          onClick={() => setTheme(resolvedTheme === "dark" ? "light" : "dark")}
        >
          {resolvedTheme === "dark" ? <Sun /> : <Moon />}
        </Button>
      </header>

      <Section title="Color tokens">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-7">
          {TOKEN_SWATCHES.map((s) => (
            <div key={s.name} className="flex flex-col gap-1">
              <div className={`h-12 rounded-lg ${s.cls}`} />
              <span className="truncate text-xs text-muted-foreground">{s.name}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Typography and radius">
        <div className="flex flex-col gap-1">
          <span className="text-3xl font-bold">Heading 3xl bold</span>
          <span className="text-xl font-semibold">Heading xl semibold</span>
          <span className="text-base">Body base</span>
          <span className="text-sm text-muted-foreground">Muted sm</span>
          <span className="text-xs text-muted-foreground">Caption xs</span>
        </div>
        <div className="flex gap-3">
          {(["rounded-sm", "rounded-md", "rounded-lg", "rounded-xl", "rounded-2xl"] as const).map(
            (r) => (
              <div key={r} className={`size-16 bg-muted ${r} grid place-items-center`}>
                <span className="text-[10px] text-muted-foreground">{r.slice(8)}</span>
              </div>
            ),
          )}
        </div>
      </Section>

      <Section title="IconBubble and StatusPill">
        <div className="flex flex-wrap items-center gap-3">
          {DOMAINS.map((d) => (
            <IconBubble key={d} domain={d}>
              <Lightbulb />
            </IconBubble>
          ))}
          {DOMAINS.filter((d) => d !== "neutral").map((d) => (
            <IconBubble key={`${d}-filled`} domain={d} filled>
              <Lightbulb />
            </IconBubble>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          <StatusPill status="ok">Connected</StatusPill>
          <StatusPill status="info">Away mode</StatusPill>
          <StatusPill status="warn">Battery low</StatusPill>
          <StatusPill status="alert">Motion detected</StatusPill>
          <StatusPill status="offline">Offline</StatusPill>
        </div>
      </Section>

      <Section title="Tile variants">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          {DOMAINS.map((d) => (
            <Tile key={d} domain={d} active interactive>
              <span className="text-sm font-medium capitalize">{d} active</span>
              <span className="text-xs text-muted-foreground">Tap me (press feedback)</span>
            </Tile>
          ))}
        </div>
      </Section>

      <Section title="GlanceCard and SensorBadge">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          <GlanceCard label="Power now" value={power} unit="W" icon={<Zap />} domain="energy" />
          <GlanceCard
            label="Lights on"
            value={lampOn ? 4 : 3}
            icon={<Lightbulb />}
            domain="light"
          />
          <GlanceCard
            label="Inside"
            value={21.4}
            precision={1}
            unit="°"
            icon={<Thermometer />}
            domain="climate"
          />
          <GlanceCard
            label="Security"
            value={0}
            unit="alerts"
            icon={<Shield />}
            domain="security"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <SensorBadge icon={<Thermometer />} value={21.4} unit="°" domain="climate" />
          <SensorBadge icon={<Droplets />} value={48} unit="%" precision={0} domain="climate" />
          <SensorBadge icon={<Activity />} value={132} unit="W" precision={0} domain="energy" />
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPower(Math.round(200 + Math.random() * 1800))}
          >
            Randomize power (watch numbers spring)
          </Button>
        </div>
      </Section>

      <Section title="DeviceTile and DimmerSlider">
        <ZoneSection
          name="Living Room"
          summary={<SensorBadge icon={<Thermometer />} value={21.4} unit="°" domain="climate" />}
        >
          <DeviceTile
            name="Ceiling Lamp"
            icon={<Lamp />}
            domain="light"
            state={lampOn ? `${Math.round(dim * 100)}%` : "Off"}
            active={lampOn}
            onToggle={() => setLampOn((v) => !v)}
          >
            {lampOn && <DimmerSlider value={dim} onChange={setDim} aria-label="Ceiling lamp dim" />}
          </DeviceTile>
          <DeviceTile
            name="TV Socket"
            icon={<Tv />}
            domain="energy"
            state="On"
            active
            onToggle={() => {}}
          />
          <DeviceTile name="Fan" icon={<Fan />} domain="climate" state="Off" onToggle={() => {}} />
          <DeviceTile name="Old Plug" icon={<Plug />} state="Off" available={false} />
        </ZoneSection>
      </Section>

      <Section title="ThermostatDial">
        <div className="flex items-center gap-8">
          <ThermostatDial
            value={targetTemp}
            measured={20.8}
            min={10}
            max={28}
            onChange={setTargetTemp}
          />
          <p className="max-w-48 text-sm text-muted-foreground">
            Drag around the dial or use arrow keys. Snaps to 0.5 degree steps.
          </p>
        </div>
      </Section>

      <Section title="FlowButton">
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
          <FlowButton
            name="Movie night"
            icon={<PartyPopper />}
            onTrigger={() => new Promise((resolve) => setTimeout(resolve, 1200))}
          />
          <FlowButton
            name="Failing flow"
            icon={<PartyPopper />}
            onTrigger={() =>
              new Promise((_, reject) => setTimeout(() => reject(new Error("flow failed")), 1200))
            }
          />
        </div>
      </Section>

      <Section title="ConnectionBanner">
        <p className="text-sm text-muted-foreground">
          The banner renders fixed at the top of the viewport (z-50). Use the buttons below to
          preview each state; it renders nothing when the connection is healthy.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button
            variant={bannerSocket === "reconnecting" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setBannerSocket("reconnecting");
              setBannerHomey("connected");
            }}
          >
            Reconnecting
          </Button>
          <Button
            variant={bannerSocket === "offline" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setBannerSocket("offline");
              setBannerHomey("connected");
            }}
          >
            Offline
          </Button>
          <Button
            variant={
              bannerSocket === "open" && bannerHomey === "disconnected" ? "default" : "outline"
            }
            size="sm"
            onClick={() => {
              setBannerSocket("open");
              setBannerHomey("disconnected");
            }}
          >
            Homey unreachable
          </Button>
          <Button
            variant={bannerSocket === "open" && bannerHomey === "connected" ? "default" : "outline"}
            size="sm"
            onClick={() => {
              setBannerSocket("open");
              setBannerHomey("connected");
            }}
          >
            Hidden (all good)
          </Button>
        </div>
        <ConnectionBanner socketStatus={bannerSocket} homeyStatus={bannerHomey} />
      </Section>

      <Section title="PillTabs">
        <p className="text-sm text-muted-foreground">
          Sliding pill highlight with motion layoutId. Arrow keys move focus and selection across
          enabled tabs only. Disabled tabs show a "soon" tooltip.
        </p>
        <PillTabs
          tabs={[
            { id: "home", label: "Home" },
            { id: "energy", label: "Energy", disabled: true },
            { id: "flows", label: "Flows", disabled: true },
            { id: "security", label: "Security", disabled: true },
          ]}
          value={pillTab}
          onChange={setPillTab}
        />
        <p className="text-xs text-muted-foreground">Active tab: {pillTab}</p>
      </Section>

      <Section title="StatusBar">
        <p className="text-sm text-muted-foreground">
          Live minute clock (1s tick), full date, outside temp badge, power badge, security pill.
        </p>
        <div className="rounded-xl bg-tile p-3">
          <StatusBar outsideTemp={12.4} powerNowW={740} securityAlerts={0} />
        </div>
        <div className="rounded-xl bg-tile p-3">
          <StatusBar outsideTemp={-2.1} powerNowW={2100} securityAlerts={2} />
        </div>
        <div className="rounded-xl bg-tile p-3">
          <StatusBar />
        </div>
      </Section>

      <Section title="ClimateTile">
        <p className="text-sm text-muted-foreground">
          Opens a thermostat dial dialog on tap. Dialog title = device name. Local dial state syncs
          from prop when the dialog opens.
        </p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <ClimateTile
            name="Living Room AC"
            state={`${climateTarget}°`}
            active
            available
            targetTemperature={climateTarget}
            measuredTemperature={20.8}
            min={16}
            max={30}
            onCommitTarget={setClimateTarget}
          />
          <ClimateTile
            name="Bedroom (read-only)"
            state="21.5°"
            active
            available
            targetTemperature={null}
            measuredTemperature={21.5}
            onCommitTarget={() => {}}
          />
          <ClimateTile
            name="Offline Heater"
            state="Unavailable"
            active={false}
            available={false}
            targetTemperature={18}
            onCommitTarget={() => {}}
          />
        </div>
      </Section>

      <Section title="EnergyGauge and InsightChart">
        <div className="flex flex-wrap items-end gap-8">
          <EnergyGauge value={power} max={3500} />
          <div className="min-w-64 flex-1">
            <InsightChart data={CHART_DATA} domain="energy" unit="W" />
          </div>
          <div className="min-w-64 flex-1">
            <InsightChart data={CHART_DATA} domain="climate" sparkline />
          </div>
        </div>
      </Section>

      <Section title="MeterRow">
        <p className="text-sm text-muted-foreground">
          Proportional fill bar with optional icon bubble, animated value, and domain color. Fill
          clamps to 100% when value exceeds max.
        </p>
        <div className="flex max-w-sm flex-col gap-4 rounded-xl bg-tile p-4">
          {/* Top consumer style: energy domain, watts */}
          <MeterRow
            label="Washing Machine"
            value={1840}
            unit="W"
            max={2200}
            domain="energy"
            icon={<Zap />}
            precision={0}
          />
          {/* Stat style: climate domain, temperature */}
          <MeterRow
            label="Living Room"
            value={21.4}
            unit="°"
            max={30}
            domain="climate"
            icon={<Thermometer />}
            precision={1}
          />
          {/* At/over max: value exceeds max, clamped to 100% */}
          <MeterRow
            label="Peak draw (clamped)"
            value={3800}
            unit="W"
            max={3500}
            domain="energy"
            icon={<Zap />}
            precision={0}
          />
        </div>
      </Section>

      <Section title="KioskClock">
        <Button variant="outline" size="sm" onClick={() => setShowClock((v) => !v)}>
          {showClock ? "Hide" : "Show"} screensaver preview
        </Button>
        {showClock && (
          <div className="h-72 overflow-hidden rounded-2xl">
            <KioskClock />
          </div>
        )}
      </Section>

      <Section title="Kiosk">
        <p className="text-sm text-muted-foreground">
          KioskToggle switches between Maximize and Minimize icon with matching aria-label. The
          screensaver preview fades in and auto-dismisses after 3s, or immediately on any
          touch/keypress (onWake). Toggle the style between clock and blank before previewing.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <KioskToggle
            isFullscreen={kioskFullscreen}
            onToggle={() => setKioskFullscreen((v) => !v)}
          />
          <span className="text-sm text-muted-foreground">
            {kioskFullscreen ? "Fullscreen (simulated)" : "Windowed"}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant={screensaverStyle === "clock" ? "default" : "outline"}
            size="sm"
            onClick={() => setScreensaverStyle("clock")}
          >
            Clock style
          </Button>
          <Button
            variant={screensaverStyle === "blank" ? "default" : "outline"}
            size="sm"
            onClick={() => setScreensaverStyle("blank")}
          >
            Blank style
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setScreensaverShow(true)}
            disabled={screensaverShow}
          >
            Preview screensaver (3s)
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Screensaver auto-dismisses after 3s. Click or press any key on it to dismiss immediately.
        </p>
        {/* The screensaver is fixed/fullscreen — it renders outside the layout flow */}
        <KioskScreensaver
          show={screensaverShow}
          style={screensaverStyle}
          onWake={() => setScreensaverShow(false)}
        />
      </Section>

      <Section title="Motion check">
        <p className="text-sm text-muted-foreground">
          Numbers spring, tiles press to 0.97, state text crossfades, zone grids stagger in. All
          animation is disabled under prefers-reduced-motion.{" "}
          <AnimatedNumber value={power} className="font-semibold" /> W live demo.
        </p>
      </Section>
    </div>
  );
}
